import { useState } from "react";
import { api, type Flag } from "../../api.ts";
import { tokens, useTheme } from "../../glassTheme.tsx";
import { GlassCard } from "../glass/Glass.tsx";
import { GlassButton } from "../glass/Button.tsx";
import { isSnoozedToday, snoozeToday } from "./flagSnooze.ts";

/**
 * One flag, one card (§1, §4). The design page's mock shows two labelled
 * options ("Option A" / "Option B") — real flags don't carry that: the
 * `flags` table has only a title, a body and a free-text `resolution`
 * (confirmed against the live board — F1/F4/F5/F7 are all prose, never
 * phrased as two named choices). So "Resolve" opens a single field for the
 * sentence that settles it, rather than two buttons whose labels would have
 * to be invented. "Not today" snoozes client-side; it never calls resolve.
 */
export function FlagCard({
  flag, today, onResolved,
}: { flag: Flag; today: string; onResolved: () => void }) {
  const { colors } = useTheme();
  const [snoozed, setSnoozed] = useState(() => isSnoozedToday(flag.id, today));
  const [resolving, setResolving] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (snoozed) return null;

  const notToday = () => { snoozeToday(flag.id, today); setSnoozed(true); };

  const resolve = async () => {
    const resolution = text.trim();
    if (!resolution || busy) return;
    setBusy(true); setError(null);
    try { await api.resolveFlag(flag.id, resolution); onResolved(); }
    catch (e: any) { setError(e?.message ?? "that did not go through"); }
    finally { setBusy(false); }
  };

  return (
    <GlassCard padding={16} style={{ marginTop: 16 }}>
      <div style={{
        fontFamily: tokens.font.sans, fontSize: tokens.type.caption.size,
        letterSpacing: tokens.type.caption.letterSpacing, textTransform: "uppercase",
        fontWeight: 600, color: colors.ink2,
      }}>Flag · one decision</div>
      <div style={{
        fontFamily: tokens.font.serif, fontSize: tokens.type.sheetTitle.size,
        lineHeight: tokens.type.sheetTitle.lineHeight, margin: "6px 0 10px", color: colors.ink,
      }}>{flag.title}</div>
      {flag.body && (
        <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.5, color: colors.ink2 }}>{flag.body}</p>
      )}

      {resolving ? (
        <div>
          <input
            autoFocus value={text} onChange={(e) => setText(e.target.value)}
            placeholder="what did you decide?"
            style={{
              width: "100%", boxSizing: "border-box", fontFamily: tokens.font.sans, fontSize: 16,
              padding: "10px 12px", borderRadius: 8, border: `1px solid ${colors.hair2}`,
              background: colors.sheet, color: colors.ink,
            }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <GlassButton variant="primary" disabled={!text.trim() || busy} onClick={resolve}>
              {busy ? "…" : "Save"}
            </GlassButton>
            <GlassButton variant="ghost" onClick={() => { setResolving(false); setText(""); }}>Cancel</GlassButton>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8 }}>
          <GlassButton variant="primary" onClick={() => setResolving(true)}>Resolve</GlassButton>
          <GlassButton variant="ghost" onClick={notToday}>Not today</GlassButton>
        </div>
      )}
      {error && <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#9C4A3C" }}>{error}</p>}
    </GlassCard>
  );
}
